import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PlayersAPI, MatchAPI, CoachTeamsAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SiteHeader from "@/components/SiteHeader";
import CoachTournaments from "@/pages/CoachTournaments";
import CoachPlayerForm from "@/components/CoachPlayerForm";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Plus,
  Minus,
  ChevronLeft,
  ChevronRight,
  Save,
  Pencil,
  LogOut,
  Users,
  Trophy,
  UserPlus,
  Shield,
  Sparkles,
  X as XIcon,
  ExternalLink,
} from "lucide-react";
import { getSport } from "@/lib/sports";

/**
 * Coach Dashboard — quick match-entry interface scoped to coach's assigned_clubs.
 * Coach picks a club → sees only its players → quick +/- buttons per stat.
 */
export default function CoachDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("players"); // 'players' | 'tournaments' | 'teams'
  const [allPlayers, setAllPlayers] = useState([]);
  const [activeClub, setActiveClub] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [pending, setPending] = useState({}); // { stat_key: delta }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: "list" }); // 'list' | 'create' | 'edit' | 'qr'
  const [myTeams, setMyTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(
    () => localStorage.getItem("ltc_coach_onboarding_dismissed") !== "1",
  );

  const clubs = user?.assigned_clubs || [];

  const dismissOnboarding = () => {
    localStorage.setItem("ltc_coach_onboarding_dismissed", "1");
    setShowOnboarding(false);
  };

  const reload = () => {
    if (clubs.length === 0) return;
    setLoading(true);
    PlayersAPI.list()
      .then((list) =>
        setAllPlayers(
          list.filter((p) => clubs.includes((p.club_name || "").trim())),
        ),
      )
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    if (!activeClub && clubs[0]) setActiveClub(clubs[0]);
  }, [user]);

  const loadMyTeams = () => {
    setTeamsLoading(true);
    CoachTeamsAPI.list()
      .then(setMyTeams)
      .catch(() => toast.error("Erreur de chargement des équipes"))
      .finally(() => setTeamsLoading(false));
  };

  useEffect(() => {
    if (tab === "teams") loadMyTeams();
  }, [tab]);

  const playersOfClub = useMemo(
    () => allPlayers.filter((p) => (p.club_name || "").trim() === activeClub),
    [allPlayers, activeClub],
  );

  const sportDef = useMemo(
    () => (selectedPlayer ? getSport(selectedPlayer.sport || "football") : null),
    [selectedPlayer],
  );

  const totalDelta = Object.values(pending).reduce(
    (acc, v) => acc + Math.abs(v),
    0,
  );

  const inc = (key, delta) => {
    setPending((p) => {
      const next = { ...p, [key]: (p[key] || 0) + delta };
      if (next[key] === 0) delete next[key];
      return next;
    });
  };

  const reset = () => setPending({});

  const save = async () => {
    if (totalDelta === 0) return;
    setSaving(true);
    try {
      const updated = await MatchAPI.record(selectedPlayer.id, pending);
      toast.success("Stats mises à jour");
      // Refresh player in the list with new stats
      setAllPlayers((list) =>
        list.map((p) => (p.id === updated.id ? updated : p)),
      );
      setSelectedPlayer(updated);
      setPending({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Empty state — no clubs assigned
  if (clubs.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <SiteHeader />
        <div className="max-w-md mx-auto px-6 pt-24 text-center">
          <h1 className="font-display text-3xl font-semibold uppercase tracking-tight mb-3">
            Aucun club assigné
          </h1>
          <p className="text-zinc-400 font-body mb-8">
            Demande à l'administrateur de Laser Tech Création de t'assigner un
            club pour pouvoir saisir des matchs.
          </p>
          <Button onClick={logout} variant="outline" className="border-white/20">
            <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-8 pb-mobile-safe">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-1">
              Espace Coach · {user?.email}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold uppercase tracking-tight">
              {tab === "players"
                ? "Saisie de match"
                : tab === "teams"
                  ? "Mon équipe"
                  : "Tournois"}
            </h1>
            <p className="text-zinc-500 text-sm font-body mt-1">
              {tab === "players"
                ? "Sélectionne un joueur et incrémente ses stats du dernier match."
                : tab === "teams"
                  ? "Gère la page publique de tes équipes assignées."
                  : "Gère tes tournois ou les classements de ton équipe."}
            </p>
          </div>
          <Button
            onClick={logout}
            variant="outline"
            size="sm"
            data-testid="coach-logout"
            className="border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Quitter
          </Button>
        </div>

        {/* Onboarding "Premiers pas" */}
        {showOnboarding && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="coach-onboarding"
            className="relative metal-card rounded-2xl p-5 mb-6 border border-[#D4AF37]/30"
            style={{
              background:
                "linear-gradient(180deg, rgba(212,175,55,0.08) 0%, rgba(212,175,55,0.02) 100%)",
            }}
          >
            <button
              type="button"
              onClick={dismissOnboarding}
              data-testid="coach-onboarding-dismiss"
              aria-label="Masquer"
              className="absolute top-3 right-3 w-7 h-7 rounded-full inline-flex items-center justify-center text-zinc-500 hover:text-white border border-white/10"
            >
              <XIcon className="w-3 h-3" />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <h3 className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-white">
                Premiers pas
              </h3>
            </div>
            <p className="text-zinc-400 text-sm font-body mb-4">
              Bienvenue dans ton Espace Coach. Voici comment animer la vie de
              tes équipes en quelques minutes :
            </p>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                {
                  n: "1",
                  t: "Personnalise ton équipe",
                  d: "Onglet Mon équipe : logo, palmarès, galerie, présentation.",
                },
                {
                  n: "2",
                  t: "Crée tes joueurs",
                  d: "Onglet Joueurs : ajoute photos, postes et numéros.",
                },
                {
                  n: "3",
                  t: "Saisis les stats du match",
                  d: "Sélectionne un joueur et incrémente buts, passes, etc.",
                },
                {
                  n: "4",
                  t: "Gère tes tournois",
                  d: "Onglet Tournois : crée ou rejoins, invite d'autres équipes.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="flex items-start gap-3 metal-card rounded-xl p-3 border border-white/5"
                >
                  <span
                    className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-black text-black"
                    style={{
                      background:
                        "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                    }}
                  >
                    {s.n}
                  </span>
                  <div className="min-w-0">
                    <div className="font-display text-sm font-bold uppercase tracking-tight">
                      {s.t}
                    </div>
                    <p className="text-zinc-500 text-xs font-body mt-0.5">
                      {s.d}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </motion.div>
        )}

        {/* Tabs: Players | Tournaments | Teams */}
        <div className="flex gap-2 mb-6 border-b border-white/10 overflow-x-auto" data-testid="coach-tabs">
          <button
            onClick={() => setTab("players")}
            data-testid="coach-tab-players"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors shrink-0"
            style={{
              borderColor: tab === "players" ? "#D4AF37" : "transparent",
              color: tab === "players" ? "#fff" : "#71717a",
            }}
          >
            <Users className="w-3.5 h-3.5" /> Joueurs
          </button>
          <button
            onClick={() => {
              setTab("tournaments");
              setSelectedPlayer(null);
              setPending({});
            }}
            data-testid="coach-tab-tournaments"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors shrink-0"
            style={{
              borderColor: tab === "tournaments" ? "#D4AF37" : "transparent",
              color: tab === "tournaments" ? "#fff" : "#71717a",
            }}
          >
            <Trophy className="w-3.5 h-3.5" /> Tournois
          </button>
          <button
            onClick={() => {
              setTab("teams");
              setSelectedPlayer(null);
              setPending({});
            }}
            data-testid="coach-tab-teams"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors shrink-0"
            style={{
              borderColor: tab === "teams" ? "#D4AF37" : "transparent",
              color: tab === "teams" ? "#fff" : "#71717a",
            }}
          >
            <Shield className="w-3.5 h-3.5" /> Mon équipe
          </button>
        </div>

        {tab === "tournaments" ? (
          <CoachTournaments user={user} />
        ) : tab === "teams" ? (
          <div data-testid="coach-teams-panel">
            <p className="text-zinc-500 text-sm font-body mb-4">
              Tes équipes assignées. Personnalise la page publique (logo,
              palmarès, galerie, présentation).
            </p>
            {teamsLoading ? (
              <div className="text-center text-zinc-500 py-12 font-body">
                Chargement…
              </div>
            ) : myTeams.length === 0 ? (
              <div className="metal-card rounded-xl p-8 text-center">
                <p className="text-zinc-400 font-body text-sm">
                  Aucune équipe trouvée pour tes clubs assignés. Demande à
                  l'administrateur de créer une équipe correspondant à un de
                  tes clubs :{" "}
                  <span className="text-white font-bold">
                    {clubs.join(", ")}
                  </span>
                  .
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {myTeams.map((t) => (
                  <div
                    key={t.id}
                    data-testid={`coach-team-${t.slug}`}
                    className="metal-card rounded-xl p-4 flex items-center gap-3 hover:border-white/30 transition-all"
                  >
                    <div
                      className="w-12 h-12 rounded-lg overflow-hidden border shrink-0 flex items-center justify-center"
                      style={{
                        borderColor: `${t.primary_color || "#0066FF"}55`,
                        background: `${t.primary_color || "#0066FF"}1F`,
                      }}
                    >
                      {t.logo_url ? (
                        <img
                          src={t.logo_url}
                          alt={t.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Shield
                          className="w-5 h-5"
                          style={{ color: t.primary_color || "#0066FF" }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold uppercase tracking-tight truncate">
                        {t.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate">
                        {[t.category, t.season].filter(Boolean).join(" · ") ||
                          getSport(t.sport || "football").label}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Link
                        to={`/coach/team/${t.id}`}
                        data-testid={`coach-team-edit-${t.slug}`}
                        title="Modifier la fiche"
                        className="w-7 h-7 rounded-md flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white"
                      >
                        <Pencil className="w-3 h-3" />
                      </Link>
                      <Link
                        to={`/team/${t.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Voir la page publique"
                        className="w-7 h-7 rounded-md flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Club tabs (if more than one assigned) */}
            {clubs.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="coach-club-tabs">
            {clubs.map((club) => (
              <button
                key={club}
                onClick={() => {
                  setActiveClub(club);
                  setSelectedPlayer(null);
                  setPending({});
                }}
                className="px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-all border"
                style={{
                  background: activeClub === club ? "rgba(255,255,255,0.1)" : "transparent",
                  borderColor: activeClub === club ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)",
                  color: activeClub === club ? "#fff" : "#a1a1aa",
                }}
              >
                {club}
              </button>
            ))}
          </div>
        )}

        {!selectedPlayer && view.mode === "create" && (
          <CoachPlayerForm
            clubs={clubs}
            onCancel={() => setView({ mode: "list" })}
            onSaved={() => {
              reload();
              setView({ mode: "list" });
            }}
          />
        )}

        {!selectedPlayer && view.mode === "edit" && (
          <CoachPlayerForm
            initial={view.player}
            clubs={clubs}
            onCancel={() => setView({ mode: "list" })}
            onSaved={() => {
              reload();
              setView({ mode: "list" });
            }}
            onDeleted={() => {
              reload();
              setView({ mode: "list" });
            }}
          />
        )}

        {!selectedPlayer && view.mode === "list" && (
          // Player picker
          <div data-testid="coach-player-picker">
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-zinc-500 text-sm font-body">
                Sélectionne un joueur pour saisir ses stats, ou crée-en un
                nouveau.
              </p>
              <Button
                onClick={() => setView({ mode: "create" })}
                data-testid="coach-new-player-btn"
                className="text-black font-bold uppercase tracking-[0.18em] text-xs h-9 px-4 shrink-0"
                style={{
                  background:
                    "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                }}
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Nouveau joueur
              </Button>
            </div>
            {loading ? (
              <div className="text-center text-zinc-500 py-12 font-body">
                Chargement des joueurs…
              </div>
            ) : playersOfClub.length === 0 ? (
              <div className="metal-card rounded-xl p-8 text-center">
                <p className="text-zinc-400 font-body text-sm mb-4">
                  Aucun joueur n'est encore enregistré dans <b>{activeClub}</b>.
                </p>
                <Button
                  onClick={() => setView({ mode: "create" })}
                  className="text-black font-bold uppercase tracking-[0.18em] text-xs h-10 px-5"
                  style={{
                    background:
                      "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                  }}
                >
                  <UserPlus className="w-3.5 h-3.5 mr-2" /> Créer le premier
                  joueur
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {playersOfClub.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`coach-player-${p.slug}`}
                    className="metal-card rounded-xl p-4 flex items-center gap-3 hover:border-white/30 transition-all"
                  >
                    <button
                      onClick={() => {
                        setSelectedPlayer(p);
                        setPending({});
                      }}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      data-testid={`coach-player-edit-stats-${p.slug}`}
                    >
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 overflow-hidden border"
                        style={{
                          background: `${p.primary_color || "#0066FF"}1F`,
                          borderColor: `${p.primary_color || "#0066FF"}55`,
                        }}
                      >
                        {p.hero_image_url ? (
                          <img
                            src={p.hero_image_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span
                            className="font-display font-semibold text-base"
                            style={{ color: p.primary_color || "#0066FF" }}
                          >
                            {p.name?.[0]}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold uppercase tracking-tight truncate">
                          {p.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate">
                          {[p.position, p.category].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      {p.jersey_number != null && (
                        <span
                          className="font-display font-semibold text-2xl tabular-nums shrink-0"
                          style={{ color: p.primary_color || "#0066FF" }}
                        >
                          #{p.jersey_number}
                        </span>
                      )}
                    </button>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={() =>
                          setView({ mode: "edit", player: p })
                        }
                        data-testid={`coach-player-edit-${p.slug}`}
                        title="Modifier la fiche"
                        className="w-7 h-7 rounded-md flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedPlayer && (
          // Match entry for selected player
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="coach-match-entry"
          >
            <button
              onClick={() => {
                setSelectedPlayer(null);
                setPending({});
              }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white mb-4"
              data-testid="coach-back-to-list"
            >
              <ChevronLeft className="w-3 h-3" /> Tous les joueurs
            </button>

            <div className="metal-card rounded-xl p-5 mb-5 flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-full overflow-hidden border-2 shrink-0"
                style={{ borderColor: selectedPlayer.primary_color || "#0066FF" }}
              >
                {selectedPlayer.hero_image_url && (
                  <img
                    src={selectedPlayer.hero_image_url}
                    alt={selectedPlayer.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl sm:text-2xl font-semibold uppercase tracking-tight truncate">
                  {selectedPlayer.name}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {selectedPlayer.position} · {selectedPlayer.club_name}
                </div>
              </div>
              <Link
                to={`/player/${selectedPlayer.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-white border border-white/10 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 shrink-0"
              >
                <Pencil className="w-3 h-3" /> Voir
              </Link>
            </div>

            {/* Stats stepper grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {sportDef.player_stats.map((field) => {
                const current = Number(selectedPlayer.stats?.[field.key] || 0);
                const delta = pending[field.key] || 0;
                const nextValue = current + delta;
                return (
                  <div
                    key={field.key}
                    className="metal-card rounded-xl p-4 flex items-center justify-between gap-3"
                    data-testid={`stat-stepper-${field.key}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-body">
                        {field.label}
                      </div>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="font-display text-2xl font-semibold tabular-nums">
                          {nextValue}
                        </span>
                        {delta !== 0 && (
                          <span
                            className="text-xs font-bold tabular-nums"
                            style={{ color: delta > 0 ? "#22C55E" : "#EF4444" }}
                          >
                            {delta > 0 ? `+${delta}` : delta}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => inc(field.key, -1)}
                        data-testid={`stat-${field.key}-minus`}
                        className="w-9 h-9 rounded-full flex items-center justify-center border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-zinc-300 transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => inc(field.key, 1)}
                        data-testid={`stat-${field.key}-plus`}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-black font-bold transition-colors"
                        style={{
                          background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                        }}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sticky save bar */}
            <div className="sticky bottom-4 metal-card rounded-xl p-3 flex items-center gap-3 border border-white/15">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-body">
                  Modifications
                </div>
                <div className="font-display font-bold tabular-nums">
                  {totalDelta} action{totalDelta > 1 ? "s" : ""}
                </div>
              </div>
              {totalDelta > 0 && (
                <Button
                  variant="ghost"
                  onClick={reset}
                  data-testid="coach-reset"
                  className="text-zinc-400 hover:text-white"
                >
                  Annuler
                </Button>
              )}
              <Button
                onClick={save}
                disabled={totalDelta === 0 || saving}
                data-testid="coach-save"
                className="text-black font-bold uppercase tracking-[0.18em] text-xs h-10 px-5"
                style={{
                  background:
                    totalDelta === 0
                      ? "rgba(255,255,255,0.1)"
                      : "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                }}
              >
                <Save className="w-3.5 h-3.5 mr-2" />
                {saving ? "Envoi..." : "Enregistrer"}
              </Button>
            </div>
          </motion.div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
